import { useRouter } from "next/router"
import { type FC, useEffect, useRef, useState } from "react"

import {
  CreateDeferredLinkDocument,
  type CreateDeferredLinkMutation,
  type CreateDeferredLinkMutationVariables,
  UserActivityEventType,
  useMarkInviteFriendsLinkOpenedMutation,
} from "../../generated/graphql"
import { graphQLClient } from "../../graphql/client"
import { normalizeInviteCode } from "../../utils/inviteCode"
import { useTrackUserActivity } from "../../utils/tracking"
import { useCommunityCustomization } from "../../utils/useCommunityCustomization"
import { MaskedIcon } from "../icons/MaskedIcon"
import { ModalBase } from "../ModalBase"
import UbiqButton, { UbiqButtonSize, UbiqButtonType } from "../UbiqButton"

const APPLE_STORE_URL = "https://apps.apple.com/se/app/werz-the-good-life/id1425626243?l=en"
const GOOGLE_PLAY_WEB_URL =
  "https://play.google.com/store/apps/details?id=at.werz&hl=en&gl=US&pli=1"
const GOOGLE_PLAY_REDIRECT_URL = "https://play.google.com/store/apps/details?id=at.werz"
const GOOGLE_PLAY_APP_DEEP_LINK_URL = "https://play.google.com/d?id=at.werz"
const GOOGLE_PLAY_REFERRER_COMMUNITY_PARAM = "communityCode"
const GOOGLE_PLAY_REFERRER_INVITE_PARAM = "inviteCode"
const AUTO_COPY_INVITE_LINK_ON_DOWNLOAD = false

const LANDING_MODAL_PANEL_STYLES =
  "w-[calc(100vw-32px)] px-4 py-10 sm:max-w-[480px] sm:px-12 flex flex-col items-center"

const INVITE_BOX_STYLES = "mb-6 w-full rounded-[4px] border border-borderPrimaryLight px-4 py-4"

interface LandingProps {
  isLoading: boolean
  error: string | null
  onJoinClick: () => void
  modalViewportStyles: string
  modalContainerStyles: string
  modalPanelStyles: string
}

const InviteValueBlock: FC<{ label: string; value: string; uppercase?: boolean }> = ({
  label,
  value,
  uppercase = false,
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <>
      <p className="mb-2 text-center text-primary-h5 uppercase">{label}</p>
      <div className={`${INVITE_BOX_STYLES} cursor-pointer`} onClick={() => void handleCopy()}>
        <p
          className={`text-center text-body-md ${copied ? "text-textPrimary" : "text-textSecondary"} ${uppercase ? "uppercase" : ""}`}
        >
          {copied ? "Copied!" : value}
        </p>
      </div>
    </>
  )
}

const StoreLinksCopy: FC<{ onAppStoreClick: (store: string) => void }> = ({ onAppStoreClick }) => (
  <p className="text-center text-body-md text-textPrimary">
    When joining, you&apos;ll automatically be synced with WERZ.
    <br />
    You can download it from the{" "}
    <a
      className="underline"
      href={APPLE_STORE_URL}
      onClick={() => onAppStoreClick("Apple")}
      rel="noreferrer"
      target="_blank"
    >
      App Store
    </a>{" "}
    or{" "}
    <a
      className="underline"
      href={GOOGLE_PLAY_WEB_URL}
      onClick={() => onAppStoreClick("Google")}
      rel="noreferrer"
      target="_blank"
    >
      Google Play
    </a>
    .
  </p>
)

const openStoreUrl = (url: string, fallbackUrl?: string) => {
  if (fallbackUrl) {
    const fallbackTimer = window.setTimeout(() => {
      if (!document.hidden) {
        window.location.href = fallbackUrl
      }
    }, 1200)

    const clearFallback = () => {
      window.clearTimeout(fallbackTimer)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearFallback()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.location.href = url
    return
  }

  const popup = window.open(url, "_blank", "noopener,noreferrer")
  if (!popup) {
    window.location.href = url
  }
}

const buildGooglePlayReferrer = ({
  communityCode,
  inviteCode,
}: {
  communityCode: string
  inviteCode: string | null
}) => {
  const params = new URLSearchParams({
    utm_source: "werz",
    [GOOGLE_PLAY_REFERRER_COMMUNITY_PARAM]: communityCode,
  })

  if (inviteCode) {
    params.set("utm_content", inviteCode)
    params.set(GOOGLE_PLAY_REFERRER_INVITE_PARAM, inviteCode)
  }

  return params.toString()
}

export const Landing: FC<LandingProps> = ({
  isLoading,
  error,
  onJoinClick,
  modalViewportStyles,
  modalContainerStyles,
  modalPanelStyles,
}) => {
  const router = useRouter()
  const customizationData = useCommunityCustomization()
  const track = useTrackUserActivity()
  const { mutate: markInviteFriendsLinkOpened } =
    useMarkInviteFriendsLinkOpenedMutation(graphQLClient)
  const [downloadDebugMessage, setDownloadDebugMessage] = useState<string | null>(null)
  const trackedInviteLinkKeyRef = useRef<string | null>(null)

  const communityCode = typeof router.query.code === "string" ? router.query.code : ""
  const query = router.asPath.split("?")[1] ?? ""
  const searchParams = new URLSearchParams(query)
  const inviteCode = searchParams.get("invite")
  const debugSkipStoreRedirect = searchParams.get("debugSkipStoreRedirect") === "1"
  const defaultInviteCode = customizationData?.inviteCode
  const explicitInviteCodeValue = normalizeInviteCode(inviteCode)
  const inviteCodeValue = explicitInviteCodeValue ?? normalizeInviteCode(defaultInviteCode)

  useEffect(() => {
    if (!communityCode || !explicitInviteCodeValue) return

    const trackingKey = `${communityCode}:${explicitInviteCodeValue}`
    if (trackedInviteLinkKeyRef.current === trackingKey) return

    trackedInviteLinkKeyRef.current = trackingKey
    markInviteFriendsLinkOpened({
      communityCode,
      inviteCode: explicitInviteCodeValue,
      source: "web-members-landing",
    })
  }, [communityCode, explicitInviteCodeValue, markInviteFriendsLinkOpened])

  if (!customizationData) return null

  const { name, styles } = customizationData
  const mobileOnly = !!styles.global.mobileOnly
  const mobileInviteLayout = styles.global.inviteGate?.mobileLayout ?? "downloadFirst"
  const continueOnMobileLabel =
    styles.global.inviteGate?.continueOnMobileLabel ?? "CONTINUE ON MOBILE"
  const logoSrc = styles.global.logo.src

  const handleAppStoreClick = (appStore: string) => {
    track({
      data: { appStore },
      type: UserActivityEventType.AppstoreLinksClicked,
    })
  }

  const handleDownloadClick = async () => {
    const clipboardUrl = `${window.location.origin}/c/${communityCode}${inviteCodeValue ? `?invite=${inviteCodeValue}` : ""}`
    setDownloadDebugMessage(null)

    const ua = navigator.userAgent
    const isIOS = /iPhone|iPad|iPod/.test(ua)
    const isAndroid = /Android/i.test(ua)
    const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
    const screenWidth = Math.round(window.screen.width / 100) * 100
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const locale = navigator.language
    const playReferrer = buildGooglePlayReferrer({
      communityCode,
      inviteCode: inviteCodeValue,
    })
    const googlePlayWebUrl = `${GOOGLE_PLAY_REDIRECT_URL}&referrer=${encodeURIComponent(playReferrer)}`
    const googlePlayAppDeepLinkUrl = `${GOOGLE_PLAY_APP_DEEP_LINK_URL}&referrer=${encodeURIComponent(playReferrer)}`
    const shouldUseGooglePlayAppDeepLink = isAndroid && isMobileDevice
    const storeUrl = isIOS
      ? APPLE_STORE_URL
      : shouldUseGooglePlayAppDeepLink
        ? googlePlayAppDeepLinkUrl
        : googlePlayWebUrl

    handleAppStoreClick(isIOS ? "Apple" : "Google")

    if (AUTO_COPY_INVITE_LINK_ON_DOWNLOAD) {
      try {
        void navigator.clipboard.writeText(clipboardUrl)
      } catch {}
    }

    const deferredLinkRequest = inviteCodeValue
      ? graphQLClient
          .request<CreateDeferredLinkMutation, CreateDeferredLinkMutationVariables>(
            CreateDeferredLinkDocument,
            {
              inviteUrl: clipboardUrl,
              communityCode,
              inviteCode: inviteCodeValue,
              screenWidth,
              timezone,
              locale,
            },
          )
          .then((result) => result.createDeferredLink)
          .catch(() => null)
      : Promise.resolve(null)

    if (debugSkipStoreRedirect) {
      const deferredLinkResult = await deferredLinkRequest
      if (deferredLinkResult === true) {
        setDownloadDebugMessage(
          "Debug mode: deferred-link registration completed and store redirect was skipped.",
        )
      } else if (deferredLinkResult === false) {
        setDownloadDebugMessage(
          "Debug mode: store redirect was skipped, but server-side deferred-link registration did not complete.",
        )
      } else {
        setDownloadDebugMessage(
          "Debug mode: store redirect was skipped. Deferred-link registration could not be verified.",
        )
      }
      return
    }

    openStoreUrl(storeUrl, shouldUseGooglePlayAppDeepLink ? googlePlayWebUrl : undefined)
  }

  return (
    <ModalBase
      dialogContainerStyles={modalContainerStyles}
      dialogPanelStyles={`${LANDING_MODAL_PANEL_STYLES} ${modalPanelStyles}`}
      dialogViewportStyles={modalViewportStyles}
      show
    >
      <MaskedIcon additionalStyle="mb-6 block mx-auto" src={logoSrc} width={96} />
      <h2 className="text-primary-h2 font-blur mb-4 text-center">{name}</h2>
      <p className="mb-6 text-center font-mono text-body-md text-textPrimary sm:text-body-lg">
        You have been invited to join
        <br />
        {name} on WERZ.
      </p>

      {mobileOnly ? (
        <>
          {inviteCodeValue ? (
            <InviteValueBlock label="Your invite code" uppercase value={inviteCodeValue} />
          ) : null}
          <UbiqButton
            buttonType={UbiqButtonType.LIGHT}
            onClick={() => void handleDownloadClick()}
            size={UbiqButtonSize.SMALL}
            title="DOWNLOAD WERZ"
            type="button"
          />
        </>
      ) : (
        <>
          <div className="hidden sm:flex flex-col items-center">
            <div className="mb-6">
              <UbiqButton
                buttonType={UbiqButtonType.LIGHT}
                disabled={isLoading}
                onClick={onJoinClick}
                size={UbiqButtonSize.MEDIUM}
                title={isLoading ? "LOADING..." : "JOIN COMMUNITY"}
                type="button"
              />
            </div>
            {error && <p className="mb-2 text-center text-body-sm text-error">{error}</p>}
            <StoreLinksCopy onAppStoreClick={handleAppStoreClick} />
            {downloadDebugMessage ? (
              <p className="mt-4 text-center text-body-sm text-textPrimary">
                {downloadDebugMessage}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col items-center sm:hidden">
            {mobileInviteLayout === "joinFirst" ? (
              <>
                <div className="mb-4">
                  <UbiqButton
                    buttonType={UbiqButtonType.LIGHT}
                    disabled={isLoading}
                    onClick={onJoinClick}
                    size={UbiqButtonSize.MEDIUM}
                    title={isLoading ? "LOADING..." : "JOIN COMMUNITY"}
                    type="button"
                  />
                </div>
                {error && <p className="mb-2 text-center text-body-sm text-error">{error}</p>}
                <button
                  className="mb-6 cursor-pointer text-center font-mono text-primary-h5 underline"
                  onClick={() => void handleDownloadClick()}
                  type="button"
                >
                  {continueOnMobileLabel}
                </button>
              </>
            ) : (
              <>
                {inviteCodeValue ? (
                  <InviteValueBlock label="Your invite code" uppercase value={inviteCodeValue} />
                ) : null}
                <div className="mb-4">
                  <UbiqButton
                    buttonType={UbiqButtonType.LIGHT}
                    onClick={() => void handleDownloadClick()}
                    size={UbiqButtonSize.SMALL}
                    title="DOWNLOAD WERZ"
                    type="button"
                  />
                </div>
                <button
                  className="mb-6 cursor-pointer text-center font-mono text-primary-h5 underline"
                  onClick={onJoinClick}
                  type="button"
                >
                  CONTINUE ON WEB
                </button>
                {inviteCodeValue ? (
                  <p className="text-center text-body-md text-textPrimary">
                    Your invite code is shown above in case you need it during onboarding.
                  </p>
                ) : null}
              </>
            )}
            {downloadDebugMessage ? (
              <p className="mt-4 text-center text-body-sm text-textPrimary">
                {downloadDebugMessage}
              </p>
            ) : null}
          </div>
        </>
      )}
    </ModalBase>
  )
}
